<template>
    <div v-if="isLoaded" class="page-wrap ep">
        <editor-bar :name="node.Name" :version="node.Version" :dirty="dirty" type-label="process" @cancel="cancel()" @save="openSave()" @height="barHeight = $event">
            <button type="button" class="ai-btn pb-btn" title="Improve with AI" @click="openAIEdit()">
                <svg viewBox="0 0 24 24" class="ep-fill"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg><span>Improve with AI</span>
            </button>
        </editor-bar>

        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <editor-mode-toggle :preview="showPreview" :changed-count="changedSteps.length" changed-label="steps" @edit="closePreview()" @preview="openPreview()" />
            <div v-if="showPreview" class="ep-preview">
                <playbook-view :preview-node="previewNode" :hide-bar="true" :changed-steps="changedSteps" />
            </div>

            <!-- v-show keeps the step editors (and their undo history) alive while previewing -->
            <div v-show="!showPreview" class="ep-layout">
                <div class="ep-main">
                    <div class="ep-fmt" :class="{ live: activeEditor }" :style="{ top: barHeight + 'px' }">
                        <span class="ep-fmt-where">{{ activeEditor ? 'Step ' + (activeIndex + 1) : 'Click into a step to format it' }}</span>
                        <div v-if="activeEditor" class="ep-fmt-btns" @mousedown="keepEditorFocus">
                            <tip-tap-toolbar :editor="activeEditor" :show-align="true" />
                        </div>
                        <!-- tblNode.ProcessLayout: null = the normal list, 'grid' = each step in a box, three to a row (Playbook.vue
                             .playbook-steps-grid, and the PDF). A dropdown if more layouts are added. -->
                        <button type="button" class="ep-collapse ep-layout-toggle" :class="{ on: node.ProcessLayout == 'grid' }" :aria-pressed="node.ProcessLayout == 'grid'" title="Show the steps as boxes, up to three to a row (see Preview)" @click="node.ProcessLayout = node.ProcessLayout == 'grid' ? null : 'grid'">
                            <svg viewBox="0 0 24 24"><rect x="3" y="4" width="7" height="7" rx="1" /><rect x="14" y="4" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                            {{ node.ProcessLayout == 'grid' ? 'Grid layout: On' : 'Grid layout' }}
                        </button>
                        <button type="button" class="ep-collapse" @click="toggleReorder()">
                            {{ reorder ? 'Show full steps' : 'Reorder steps' }}
                            <svg viewBox="0 0 24 24"><path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" /></svg>
                        </button>
                    </div>

                    <article class="ep-paper">
                        <editor-doc-head :locs="locs" :deps="deps" :types="types" :owner-names="ownerNames" :version="node.Version" @open="showDetails = true" />


                        <!-- Reorder mode: one line per step, drag or use the arrows -->
                        <div v-if="reorder" class="ep-steps mini">
                            <draggable v-model="processJSON.items">
                                <div v-for="(item, i) in processJSON.items" :key="keyOf(item)" class="ep-mini">
                                    <svg class="ep-grip" viewBox="0 0 24 24"><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></svg>
                                    <span class="ep-n">{{ i + 1 }}</span>
                                    <span class="ep-mini-t">{{ item.header || 'Untitled step' }}</span>
                                    <div class="ep-tools">
                                        <button type="button" :disabled="i == 0" :aria-label="'Move step ' + (i + 1) + ' up'" @click="moveItem(i, -1)"><svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" /></svg></button>
                                        <button type="button" :disabled="i == processJSON.items.length - 1" :aria-label="'Move step ' + (i + 1) + ' down'" @click="moveItem(i, 1)"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg></button>
                                        <button type="button" class="del" :aria-label="'Delete step ' + (i + 1)" @click="removeItem(i)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                    </div>
                                </div>
                            </draggable>
                        </div>

                        <div v-else class="ep-steps">
                            <template v-for="(item, i) in processJSON.items" :key="keyOf(item)">
                                <div class="ep-ins">
                                    <button type="button" :aria-label="'Add a step before step ' + (i + 1)" @click="addItem(i)">
                                        <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>Add step
                                    </button>
                                </div>
                                <section :id="'ep-step-' + i" :class="['ep-step', { noline: item.hideSectionLine }]" @focusin="focusStep = i">
                                    <div class="ep-tools" :aria-label="'Step ' + (i + 1) + ' options'">
                                        <button type="button" :disabled="i == 0" :aria-label="'Move step ' + (i + 1) + ' up'" @click="moveItem(i, -1)"><svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" /></svg></button>
                                        <button type="button" :disabled="i == processJSON.items.length - 1" :aria-label="'Move step ' + (i + 1) + ' down'" @click="moveItem(i, 1)"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg></button>
                                        <button type="button" class="del" :aria-label="'Delete step ' + (i + 1)" @click="removeItem(i)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                    </div>
                                    <input v-model="item.header" class="ep-h" placeholder="Step title" :aria-label="'Step ' + (i + 1) + ' title'" @keydown.enter.prevent="focusBody(i)" />
                                    <div class="ep-body">
                                        <tip-tap v-model="item.body" :hide-toolbar="true" :show-align="true" @focus="onEditorFocus($event, item)" />
                                    </div>
                                    <div v-if="item.image || item.image2" class="ep-pics">
                                        <template v-for="slot in ['image', 'image2']" :key="slot">
                                            <div v-if="item[slot]" class="ep-pic">
                                                <img :src="item[slot]" :alt="'Image for step ' + (i + 1)" />
                                                <button type="button" class="ep-x" aria-label="Remove image" @click="item[slot] = null"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
                                            </div>
                                        </template>
                                    </div>
                                    <div class="ep-chips">
                                        <label v-if="!item.image || !item.image2" :class="['ep-chip', { busy: uploadingKey == keyOf(item) }]">
                                            <input type="file" accept="image/*" hidden @change="onPickImage($event, item)" />
                                            <svg viewBox="0 0 24 24"><path d="M15 8h.01M12 21H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v6M3 16l5-5c.93-.9 2.07-.9 3 0l4 4M14 14l1-1c.67-.64 1.33-.84 2-.6M16 19h6M19 16v6" /></svg>{{ uploadingKey == keyOf(item) ? 'Uploading…' : 'Image' }}
                                        </label>
                                        <label v-if="item.video || videoOpenKey == keyOf(item)" class="ep-chip ep-vid">
                                            <svg viewBox="0 0 24 24"><path d="M15 10l4.55-2.28A1 1 0 0 1 21 8.62v6.76a1 1 0 0 1-1.45.9L15 14M5 6h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" /></svg>
                                            <input :ref="'video-' + keyOf(item)" v-model="item.video" placeholder="Paste a video link" @input="checkVideoLink(item)" />
                                        </label>
                                        <button v-else type="button" class="ep-chip" @click="openVideo(item)">
                                            <svg viewBox="0 0 24 24"><path d="M15 10l4.55-2.28A1 1 0 0 1 21 8.62v6.76a1 1 0 0 1-1.45.9L15 14M5 6h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM9 10v4M7 12h4" /></svg>Video
                                        </button>
                                        <button type="button" :class="['ep-chip', { on: item.hideSectionLine }]" :aria-pressed="!!item.hideSectionLine" @click="item.hideSectionLine = !item.hideSectionLine">
                                            <svg viewBox="0 0 24 24"><path :d="item.hideSectionLine ? 'M5 12h2M11 12h2M17 12h2' : 'M5 12h14'" /></svg>{{ item.hideSectionLine ? 'Section line hidden' : 'Section line' }}
                                        </button>
                                    </div>
                                </section>
                            </template>
                            <p v-if="!processJSON.items.length" class="ep-empty">No steps yet.</p>
                        </div>
                        <button type="button" class="ep-addlast" @click="addItem(processJSON.items.length)">
                            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>Add step
                        </button>
                    </article>
                </div>

                <aside class="ep-side" aria-label="Outline" :style="{ top: barHeight + 'px' }">
                    <div class="ep-side-h">Outline</div>
                    <button v-for="(item, i) in processJSON.items" :key="keyOf(item)" type="button" :class="['ep-ol', { on: focusStep == i }]" @click="jumpTo(i)">
                        <span>{{ i + 1 }}</span>{{ item.header || 'Untitled step' }}
                    </button>
                    <editor-activity :comments="comments" :signed-count="signedCount" :required-count="requiredSigners.length" :version="node.Version" />
                </aside>
            </div>
        </div>

        <editor-details-drawer v-if="showDetails" v-model:name="node.Name" v-model:ownerIds="ownerIDs" v-model:locs="locs" v-model:deps="deps" v-model:types="types" type-label="process" @close="showDetails = false" />
        <editor-save-dialog v-if="showSave" v-model:status="node.NeedsApprovalType" :version="node.Version" :required-count="requiredSigners.length" :signed-count="signedCount" :busy="disableButton" note-placeholder="e.g. Clarified when to hold the order in step 3" @cancel="showSave = false" @save="submitCheck($event)" />
        <editor-discard-dialog v-if="showDiscard" @keep="showDiscard = false" @discard="leave()" />

        <confirm-delete v-if="showAIConfirm" @confirm="submit()" @cancel="() => { showAIConfirm = false; }" :msg="AIConfirmText"/>
        <!-- One "Improve with AI" panel: chat, tune-up or update from a recording (Keelan, 2 Nov 2026) -->
        <ai-improve-panel v-if="aiEditDraft" node-type="process" :slug="node.Slug" :current-draft="{ name: node.Name, items: processJSON.items, changesSummary: [] }" :preview-base-node="currentPreviewNode()" @close="aiEditDraft = null" @apply="d => { onAIDraft(d); aiEditDraft = aiEditDraft || {} }" @accept="onAIDraft" />
    </div>
</template>

<script lang="ts">
import { defineComponent, toRaw } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next'
import TipTap from '@/components/TipTap.vue';
import TipTapToolbar from '@/components/TipTapToolbar.vue';
import AIImprovePanel from '@/components/AIImprovePanel.vue';
import PlaybookView from '@/views/Playbook.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import EditorBar from '@/components/EditorBar.vue';
import EditorModeToggle from '@/components/EditorModeToggle.vue';
import EditorDocHead from '@/components/EditorDocHead.vue';
import EditorActivity from '@/components/EditorActivity.vue';
import EditorDetailsDrawer from '@/components/EditorDetailsDrawer.vue';
import EditorSaveDialog from '@/components/EditorSaveDialog.vue';
import EditorDiscardDialog from '@/components/EditorDiscardDialog.vue';
import nodeEditor from '@/nodeEditor';
import { processDraftItems } from '@/ai/processDraft';

// Stable v-for keys for steps without adding anything to the saved ProcessJSON
const itemKeys = new WeakMap<object, number>();
let nextItemKey = 1;

// What counts as a change: the fields the page shows, ignoring false vs. missing
function normalizeItem(item: any) {
    return {
        header: item.header || '',
        body: item.body || '',
        image: item.image || null,
        image2: item.image2 || null,
        video: item.video || '',
        hideSectionLine: !!item.hideSectionLine
    };
}

export default defineComponent({
    name: 'EditProcess',
    mixins: [ nodeEditor ],
    components: {
        PlaybookView,
        draggable: VueDraggableNext,
        TipTap,
        TipTapToolbar,
        AiImprovePanel: AIImprovePanel,
        ConfirmDelete,
        EditorBar,
        EditorModeToggle,
        EditorDocHead,
        EditorActivity,
        EditorDetailsDrawer,
        EditorSaveDialog,
        EditorDiscardDialog
    },
    data() {
        return {
            processJSON: { items: [] } as any,
            savedItems: [] as any[],
            reorder: false,
            activeEditor: null as any,
            activeItem: null as any,
            focusStep: -1,
            uploadingKey: 0,
            videoOpenKey: 0,
            aiEditDraft: null as any,
            showAIModal: false,
            showAIVideoModal: false
        }
    },
    computed: {
        // Step indexes that differ from the saved version, marked in Preview
        changedSteps(): number[] {
            return (this.processJSON.items || []).reduce((out: number[], item: any, i: number) => {
                const before = this.savedItems[i];
                if(!before || JSON.stringify(normalizeItem(item)) != JSON.stringify(before)) out.push(i);
                return out;
            }, []);
        },
        activeIndex(): number {
            return (this.processJSON.items || []).findIndex((item: any) => toRaw(item) === toRaw(this.activeItem));
        }
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        this.guardLeave(next);
    },
    methods: {
        // nodeEditor content hooks
        contentField(): string {
            return 'ProcessJSON';
        },
        loadContent(json: any) {
            this.processJSON = json;
        },
        contentData(): any {
            return this.processJSON;
        },
        contentSnapshot(): any {
            return (this.processJSON.items || []).map(normalizeItem);
        },
        onMarkSaved() {
            this.savedItems = (this.processJSON.items || []).map(normalizeItem);
        },
        typeTitle(): string {
            return 'Process';
        },

        keyOf(item: any): number {
            const raw = toRaw(item);
            if(!itemKeys.has(raw)) itemKeys.set(raw, nextItemKey++);
            return itemKeys.get(raw) as number;
        },
        currentPreviewNode() {
            return this.nodeWithEdits();
        },
        openAIEdit() {
            this.aiEditDraft = JSON.parse(JSON.stringify({ name: this.node.Name, items: this.processJSON.items, changesSummary: [] }));
        },
        addItem(index: number) {
            this.processJSON.items.splice(index, 0, {
                header: '',
                body: '',
                image: ''
            })
            if(this.reorder) {
                return;
            }
            this.$nextTick(() => this.focusHeader(index));
        },
        moveItem(i: number, dir: number) {
            const items = this.processJSON.items;
            const j = i + dir;
            if(j < 0 || j >= items.length) return;
            [items[i], items[j]] = [items[j], items[i]];
            this.focusStep = j;
        },
        removeItem(i: number) {
            const [removed] = this.processJSON.items.splice(i, 1);
            if(toRaw(removed) === toRaw(this.activeItem)) {
                this.activeEditor = null;
                this.activeItem = null;
            }
        },
        toggleReorder() {
            // The step editors are rebuilt when leaving reorder mode
            this.activeEditor = null;
            this.activeItem = null;
            this.reorder = !this.reorder;
        },
        onEditorFocus(editor: any, item: any) {
            this.activeEditor = editor;
            this.activeItem = item;
        },
        // Clicking a toolbar button shouldn't take the cursor out of the step being formatted
        keepEditorFocus(e: any) {
            if(e.target.tagName != 'INPUT' && e.target.tagName != 'SELECT') e.preventDefault();
        },
        focusHeader(i: number) {
            const el = document.querySelector(`#ep-step-${i} .ep-h`) as HTMLInputElement;
            if(el) {
                el.focus();
                el.scrollIntoView({ block: 'center' });
            }
        },
        focusBody(i: number) {
            const el = document.querySelector(`#ep-step-${i} .ProseMirror`) as HTMLElement;
            if(el) el.focus();
        },
        jumpTo(i: number) {
            const go = () => {
                const el = document.getElementById('ep-step-' + i);
                if(el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    this.focusStep = i;
                }
            };
            if(this.showPreview || this.reorder) {
                this.showPreview = false;
                this.reorder = false;
                this.$nextTick(go);
            } else {
                go();
            }
        },
        openVideo(item: any) {
            this.videoOpenKey = this.keyOf(item);
            this.$nextTick(() => {
                const ref: any = this.$refs['video-' + this.videoOpenKey];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if(el) el.focus();
            });
        },
        async onPickImage(e: any, item: any) {
            const file = e.target.files && e.target.files[0];
            e.target.value = '';
            if(!file) return;
            const field = item.image ? 'image2' : 'image';
            this.uploadingKey = this.keyOf(item);
            const url = await this.uploadImage(file);
            if(url) item[field] = url;
            this.uploadingKey = 0;
        },
        async onAIDraft(draft: any, jobId?: any) {
            // A capture job is consumed first: its recording becomes a Video node and the
            // draft's "watch the original" link should point there before the editor loads it
            let videoSlug: string | null = null;
            if (jobId) {
                videoSlug = await axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${jobId}/consume`, { slug: this.node.Slug, name: draft.name })
                    .then(resp => resp?.data?.videoSlug || null)
                    .catch(() => null); // non-fatal
            }
            const relink = (html: string) => videoSlug && html
                ? html.replace(/href="\/api\/appfile\/[^"]*ai_recording_[^"]*"/g, `href="/playbook/${videoSlug}"`)
                : html;
            // Preserve images/videos from existing sections when headers still match
            const oldItems = [...(this.processJSON.items || [])];
            this.node.Name = draft.name;
            this.activeEditor = null;
            this.activeItem = null;
            this.processJSON.items = processDraftItems(draft.items, oldItems).map(item => ({ ...item, body: relink(item.body) }));
            this.aiEditDraft = null;
            this.showAIModal = false;
            this.showAIVideoModal = false;
        },
        checkVideoLink(item: any) {
            item.video = this.convertVideoEmbedLink(item.video);
        },
    }
});
</script>
